import React, { useState, useEffect } from 'react';

import { Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

import { appConfig } from '../config.js';
import { formatDatesInChartTiks } from '../Utils.js';


const SolarChart = ({ cells, selectedCells, fromDate, toDate, token }) => {

    const [dataChart, setDataChart] = useState({});
    const [controllers, setControllers] = useState([]);

    const baseURL = appConfig.api.URL;
    const strFromDate = dateToString(fromDate);
    const strToDate = dateToString(toDate);

    let deviceNames = [];
    if (selectedCells.length > 0) {
        deviceNames = selectedCells;
    } else {
        cells.forEach((o) => deviceNames.push(o.device_name)) 
    }
    const is_aggregated = selectedCells.length !== 1;

    function dateToString(inDate) {
        let dateUTC = new Date(Date.UTC(inDate.getFullYear(), inDate.getMonth(), inDate.getDate()));
        let strDate = dateUTC.toISOString().slice(0, 10);
        return strDate;
    }

    function isRangeLargerThan(date1, date2, days) {
        if (!date1) return true;
        if (!date2) {
            date2 = new Date();
        }
        const diffTime = Math.abs(date2 - date1);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        return (diffDays > days);
    }

    function getDataPerController(data){
        /*
        dataChart = {
            "battery_voltage": {
                "2021-08-30T22:00:00Z:": {
                    "datetime_metric": "2021-07-15:,
                    "011C93": 95,
                    "011C93": 96,
                }
            }
        }
        */
        controllers.length = 0;
        const dataPerController = {};
        for (let i = 0; i < data.length; i++) {
            let metric = data[i];
            if(controllers.indexOf(metric.controller_serial_number) === -1) controllers.push(metric.controller_serial_number);
            for (let column of columns) {
                if (!dataPerController[column.field]) {
                    dataPerController[column.field] = {};
                }
                if (!dataPerController[column.field][metric.datetime_metric]) {
                    dataPerController[column.field][metric.datetime_metric] = { 'datetime_metric': new Date(metric.datetime_metric) };
                }
                dataPerController[column.field][metric.datetime_metric][metric.controller_serial_number] = parseFloat(metric[column.field]);
            }
        }
        setControllers(controllers.sort());

        /*
        dataChart = {
            "battery_voltage": [
                {
                    "datetime_metric": "2021-07-15:,
                    "O11C93": 95,
                    "O11C93": 96,
                }
            ]
        }
        */
        if (Object.keys(dataPerController).length > 0) {
            let dataChart = {};
            for (let column of columns) {
                if (!dataChart[column.field]) {
                    dataChart[column.field] = [];
                }
                for (const [date, values] of Object.entries(dataPerController[column.field])) {
                    dataChart[column.field].push(values);
                }
            }
            return dataChart;
        }
    }

    function getDataPerCell(data){
        /*
        dataChart = {
            "battery_voltage": {
                "2021-08-30T22:00:00Z:": {
                    "datetime_metric": "2021-07-15:,
                    "MSAV25014_Mapheelle": 95,
                    "MSAV25019_Ngqodoza": 96,
                }
            }
        }
        */
        const dataPerCell = {};
        for (let i = 0; i < data.length; i++) {
            let metric = data[i];
            for (let column of columns) {
                if (!dataPerCell[column.field]) {
                    dataPerCell[column.field] = {};
                }
                if (!dataPerCell[column.field][metric.datetime_metric]) {
                    dataPerCell[column.field][metric.datetime_metric] = { 'datetime_metric': new Date(metric.datetime_metric) };
                }
                dataPerCell[column.field][metric.datetime_metric][metric.cell_device_name] = parseFloat(metric[column.field]);
            }
        }

        /*
        dataChart = {
            "battery_voltage": [
                {
                    "datetime_metric": "2021-07-15:,
                    "MSAV25014_Mapheelle": 95,
                    "MSAV25019_Ngqodoza": 96,
                }
            ]
        }
        */
        if (Object.keys(dataPerCell).length > 0) {
            let dataChart = {};
            for (let column of columns) {
                if (!dataChart[column.field]) {
                    dataChart[column.field] = [];
                }
                for (const [date, values] of Object.entries(dataPerCell[column.field])) {
                    dataChart[column.field].push(values);
                }
            }
            return dataChart;
        }
    }

    useEffect(() => {
        let url = `${baseURL}/solar-metrics/?device_names=${deviceNames.join(',')}`;
        if (is_aggregated) url = `${url}&is_aggregated`;
        if (fromDate !== null) {
            url = `${url}&from_date=${strFromDate}`;
            if (toDate != null) {
                url = `${url}&to_date=${strToDate}`;
            }
        }
        if (isRangeLargerThan(fromDate, toDate, 7) || deviceNames.length > 3) {
            url = `${url}&metric_type=1`;
        }
        console.log(url);

        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Token ${token}`
            }
        })
            .then(results => results.json())
            .then(data => {
                let chartData;
                if(is_aggregated) {
                    chartData = getDataPerCell(data);
                } else {
                    chartData = getDataPerController(data);
                }
                console.log("dataChart", chartData);
                setDataChart(chartData);
            }
            )

    }, [selectedCells, fromDate, toDate])

    const columns = [
        { title: "Battery Current", field: "battery_current" },
        { title: "Battery Voltage", field: "battery_voltage" },
        { title: "Battery Temperature", field: "battery_temperature" },
        { title: "Solar 1 Voltage", field: "solar1_voltage" },
        { title: "Solar 1 Current", field: "solar1_current" },
        { title: "Solar 2 Voltage", field: "solar2_voltage" },
        { title: "Solar 2 Current", field: "solar2_current" },
        { title: "Battery Parallel Voltage", field: "battery_parallel_voltage" },
        { title: "Battery Parallel Current", field: "battery_parallel_current" },
        { title: "Load 1 Voltage", field: "load1_voltage" },
        { title: "Load 1 Current", field: "load1_current" },
        { title: "Load 2 Voltage", field: "load2_voltage" },
        { title: "Load 2 Current", field: "load2_current" },
        { title: "Power Uptime (%)", field: "power_uptime" },
        { title: "Average Power Consumed (W)", field: "avg_power_consumed" },
        { title: "Energy Consumed (Wh)", field: "energy_consumed" },
        { title: "Energy Generated (Wh)", field: "energy_generated" },
        { title: "Potential Energy Generated (Ah)", field: "pot_energy_generated" },
        ]

    const colors = [];
    let numColors = Math.max(controllers.length, deviceNames.length);
    for (let i=0; i<numColors; i++){
        colors.push('#'+(0x1000000+Math.random()*0xffffff).toString(16).substr(1,6));
    }

    if(!dataChart || Object.keys(dataChart).length === 0) {
        return <div><p>No solar data are availabe for cells: {deviceNames.join(', ')} <br />
        for the dates range: {dateToString(fromDate)}/{dateToString(toDate)}</p></div>
    }

    const getChartLines = () => {
        if (is_aggregated){
            return (
                deviceNames.map((cell, index) => 
                    <Line type="monotone" connectNulls={true} dataKey={cell} stroke={colors[index]} activeDot={{ r: 5 }} />
                )
            )
        }
        return (
            controllers.map((controller, index) => 
                <Line type="monotone" connectNulls={true} dataKey={controller} stroke={colors[index]} activeDot={{ r: 5 }} />
            )
        )
    }
    return (
        <div>
            <p>Note: displayed metrics are at hourly intervals if dates range is within 7 days 
                and there are less than 4 selected cells. Otherwise the metrics are at daily intervals.</p>
            <p>Data Source: Clear Blue</p>
            {columns.map(column =>
                <div key={`${column.field}-${fromDate}-${toDate}`}>
                    <div>{column.title}</div>
                    <LineChart
                        width={1000}
                        height={500}
                        data={dataChart[column.field]}
                        margin={{ top: 20, right: 30, left: 20, bottom: 35 }}
                    >
                        <XAxis dataKey="datetime_metric" 
                               tickFormatter={formatDatesInChartTiks}
                               reversed 
                        />
                        <YAxis />
                        <Tooltip itemSorter={item => -item.value}/>
                        <Legend />
                        <CartesianGrid stroke="#f5f5f5" strokeDasharray="3 3" />
                        {getChartLines()}
                    </LineChart>
                </div>
            )}
        </div>
    );
};

export default SolarChart;